// src/autopilotMode/services/SecurityRiskService.js

import { v4 as uuid } from 'uuid';
import { authFetch } from '../../utils/authFetch';
import {
  saveScenario,
  persistScenarioAgents,
  persistScenarioAttackSurface,
  persistScenarioImpact,
  saveSecurityControl,
} from '../../redux/slices/securitySlice';

/**
 * SecurityRiskService - Automated security threat assessment for autopilot mode
 *
 * Orchestrates:
 * 1. AI-generated threat scenario creation
 * 2. Threat level assessment
 * 3. Security control generation
 *
 * Follows same pattern as RiskDiscoveryServiceV2, ActionPlanningService, ResidualRiskService
 */
export class SecurityRiskService {
  constructor(dispatch, modelId, assessmentUuid, liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.liveUpdateCallback = liveUpdateCallback;
  }

  /**
   * Execute complete security assessment
   *
   * @param {Object} contextData - Approved narratives from context step
   * @param {Object} riskFormData - Risk findings from 6 completed areas
   * @param {string} modelType - 'generative' or 'predictive'
   * @param {Function} progressCallback - Progress update callback
   * @returns {Object} Assessment results
   */
  async executeSecurityAssessment(contextData, riskFormData, modelType, progressCallback) {
    try {
      console.log('[SecurityRiskService] Starting security assessment for model:', this.modelId);

      // Phase 1: Generate Threat Scenarios (1 API call, ~3-5s)
      if (progressCallback) progressCallback('generating scenarios');
      console.log('[SecurityRiskService] Phase 1: Generating threat scenarios...');

      const scenariosResponse = await authFetch('/.netlify/functions/generateSecurityScenarios', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId: this.modelId,
          assessmentUuid: this.assessmentUuid,
          modelType,
          contextData,
          riskFormData
        })
      });

      if (!scenariosResponse.ok) {
        const errorText = await scenariosResponse.text().catch(() => 'Unknown error');
        throw new Error(`Scenario generation failed: ${errorText}`);
      }

      const { scenarios } = await scenariosResponse.json();

      if (!Array.isArray(scenarios) || scenarios.length === 0) {
        console.warn('[SecurityRiskService] No scenarios generated, skipping security assessment');
        return { scenarios: 0, totalControls: 0 };
      }

      console.log(`[SecurityRiskService] Generated ${scenarios.length} threat scenarios`);

      // LIVE UPDATE: Scenarios generated
      if (this.liveUpdateCallback) {
        this.liveUpdateCallback('scenariosGenerated', {
          count: scenarios.length,
          scenarios: scenarios.map(s => ({ name: s.name, description: s.description }))
        });
      }

      // Phase 2-4: Process each scenario
      const results = [];

      for (let i = 0; i < scenarios.length; i++) {
        const scenario = scenarios[i];
        const scenarioId = uuid();

        try {
          console.log(`[SecurityRiskService] Processing scenario ${i + 1}/${scenarios.length}: ${scenario.name}`);

          // Phase 2: Save Scenario & Populate Attributes
          if (progressCallback) progressCallback(`saving scenario ${i + 1}/${scenarios.length}`);

          // Save scenario to Redux/DB
          await this.dispatch(saveScenario({
            modelId: this.modelId,
            scenarioId,
            name: scenario.name,
            mappedAgents: scenario.threatAgents || [],
            attributes: scenario.attributes || {},
            assessmentUuid: this.assessmentUuid,
            status: 'active'
          }));

          console.log(`[SecurityRiskService] Scenario saved: ${scenarioId}`);

          // Update threat agents
          if (scenario.threatAgents && scenario.threatAgents.length > 0) {
            await this.dispatch(persistScenarioAgents({
              modelId: this.modelId,
              assessmentUuid: this.assessmentUuid,
              scenarioId,
              mappedAgents: scenario.threatAgents,
            }));
          }

          // Update attack surface
          if (scenario.attackSurface) {
            await this.dispatch(persistScenarioAttackSurface({
              modelId: this.modelId,
              assessmentUuid: this.assessmentUuid,
              scenarioId,
              attackSurfaceUpdates: {
                category: scenario.attackSurface.category,
                vulnerabilities: scenario.attackSurface.vulnerabilities || [],
                description: scenario.description
              },
            }));
          }

          // Update impact areas
          if (scenario.impactAreas && Array.isArray(scenario.impactAreas)) {
            // Build impact updates object for all impact areas
            const impactUpdates = {};
            scenario.impactAreas.forEach(impactArea => {
              impactUpdates[impactArea] = 'High'; // Default, will be refined in threat assessment
            });

            await this.dispatch(persistScenarioImpact({
              modelId: this.modelId,
              assessmentUuid: this.assessmentUuid,
              scenarioId,
              impactUpdates,
            }));
          }

          // Phase 3: Assess Threat Levels (LLM-based assessment)
          if (progressCallback) progressCallback(`assessing threats ${i + 1}/${scenarios.length}`);
          console.log(`[SecurityRiskService] Assessing threat levels for scenario: ${scenario.name}`);

          // Get attack surface and impact from Redux state after updates
          const getState = () => this.dispatch((dispatch, getState) => getState());
          const currentState = getState();

          // Get scenario data from RTK security slice (byAssessment structure)
          const scenarioData = currentState.security?.byAssessment?.[this.assessmentUuid]?.scenarios?.[scenarioId] || {};

          console.log(`[SecurityRiskService] Scenario state loaded from RTK security slice`);

          const threatResponse = await authFetch('/.netlify/functions/assessSecurityThreat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              scenarioName: scenario.name,
              attackSurface: scenarioData.attackSurface || scenario.attackSurface || {},
              impact: scenarioData.impact || {},
              mappedAgents: scenarioData.mappedAgents || scenario.threatAgents || [],
              attributes: scenarioData.attributes || scenario.attributes || {}
            })
          });

          let threatAssessment = {
            threatLevel: 'Moderate',
            vulnerability_score: 5,
            impact_score: 5,
            calculated_score: 25,
            rationale: 'Default assessment - API call failed'
          };

          if (threatResponse.ok) {
            const result = await threatResponse.json();
            threatAssessment = {
              threatLevel: result.threat_level,
              vulnerability_score: result.vulnerability_score,
              impact_score: result.impact_score,
              calculated_score: result.calculated_score,
              rationale: result.rationale
            };

            console.log(`[SecurityRiskService] LLM threat assessment complete:`, {
              threatLevel: threatAssessment.threatLevel,
              calculatedScore: threatAssessment.calculated_score
            });

            // Save threat assessment to database
            await this.dispatch(saveScenario({
              modelId: this.modelId,
              scenarioId,
              assessmentUuid: this.assessmentUuid,
              threatAssessment: {
                threat_level: threatAssessment.threatLevel,
                calculated_score: threatAssessment.calculated_score,
                vulnerability_score: threatAssessment.vulnerability_score,
                impact_score: threatAssessment.impact_score
              }
            }));
            console.log(`[SecurityRiskService] Threat level saved to database: ${threatAssessment.threatLevel}`);
          } else {
            console.warn('[SecurityRiskService] Threat assessment API failed, using defaults');
          }

          // LIVE UPDATE: Scenario evaluated
          if (this.liveUpdateCallback) {
            this.liveUpdateCallback('scenarioEvaluated', {
              scenarioName: scenario.name,
              scenarioIndex: i + 1,
              totalScenarios: scenarios.length,
              threatLevel: threatAssessment.threatLevel,
              vulnerabilityScore: threatAssessment.vulnerability_score,
              impactScore: threatAssessment.impact_score,
              calculatedScore: threatAssessment.calculated_score
            });
          }

          // Phase 4: Generate Security Controls
          if (progressCallback) progressCallback(`generating controls ${i + 1}/${scenarios.length}`);
          console.log(`[SecurityRiskService] Generating security controls for scenario: ${scenario.name}`);

          const controlsResponse = await authFetch('/.netlify/functions/generateSecurityControls', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              scenarios: [{
                scenario_id: scenarioId,
                scenario_name: scenario.name,
                description: scenario.description,
                threat_level: threatAssessment.threatLevel,
                vulnerability_score: threatAssessment.vulnerability_score,
                impact_score: threatAssessment.impact_score,
                calculated_score: threatAssessment.calculated_score,
                rationale: threatAssessment.rationale,
                context: {
                  modelType,
                  riskNarrative: contextData?.riskNarrative || '',
                  impactAreas: scenario.impactAreas,
                  threatAgents: scenario.threatAgents
                }
              }]
            })
          });

          let controlsCount = 0;

          if (controlsResponse.ok) {
            const controlsData = await controlsResponse.json();

            // Auto-save top 3 controls
            if (controlsData[0]?.controls && Array.isArray(controlsData[0].controls)) {
              const topControls = controlsData[0].controls.slice(0, 3);

              for (const control of topControls) {
                await this.dispatch(saveSecurityControl({
                  controlData: control,
                  modelId: this.modelId,
                  scenarioId,
                  assessmentUuid: this.assessmentUuid
                }));
              }

              controlsCount = topControls.length;
              console.log(`[SecurityRiskService] Saved ${controlsCount} controls for scenario: ${scenario.name}`);

              // LIVE UPDATE: Controls generated
              if (this.liveUpdateCallback) {
                this.liveUpdateCallback('securityControlsGenerated', {
                  scenarioName: scenario.name,
                  controlsCount: controlsCount,
                  controls: topControls.map(c => ({ name: c.name }))
                });
              }
            }
          } else {
            console.warn('[SecurityRiskService] Control generation API failed for scenario:', scenario.name);
          }

          // Store results
          results.push({
            scenarioId,
            scenarioName: scenario.name,
            threatAssessment,
            controlsCount
          });

        } catch (scenarioError) {
          console.error(`[SecurityRiskService] Failed to process scenario "${scenario.name}":`, scenarioError);
          // Continue with other scenarios instead of failing completely
        }
      }

      // Summary
      const totalControls = results.reduce((sum, r) => sum + r.controlsCount, 0);
      console.log(`[SecurityRiskService] ✅ Security assessment complete:`, {
        scenariosProcessed: results.length,
        totalScenarios: scenarios.length,
        totalControls
      });

      return {
        scenarios: results.length,
        totalControls,
        results
      };

    } catch (error) {
      console.error('[SecurityRiskService] Security assessment failed:', error);
      throw error;
    }
  }
}
